import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CallToAction from './CallToAction';
import { Link } from 'react-router-dom';
import { Quote } from 'lucide-react';

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Split content by [CTA] placeholder
  const parts = content.split(/\[CTA\]/g);

  const components: any = {
    h2: ({ node, ...props }: any) => (
      <h2 className="text-3xl font-bold mt-12 mb-6 text-foreground scroll-mt-24 tracking-tight" id={props.children?.[0]?.toString().toLowerCase().replace(/\s+/g, '-')}>
        {props.children}
      </h2>
    ),
    h3: ({ node, ...props }: any) => (
      <h3 className="text-xl font-bold mt-8 mb-4 text-foreground scroll-mt-24" id={props.children?.[0]?.toString().toLowerCase().replace(/\s+/g, '-')}>
        {props.children}
      </h3>
    ),
    p: ({ node, ...props }: any) => {
       // If a paragraph contains only an image, don't wrap it in p tags to avoid nesting issues if needed, 
       // but standard p is usually fine.
       if (node?.children[0]?.tagName === 'figure') return <>{props.children}</>;
       return <p className="text-lg leading-8 text-muted-foreground mb-6" {...props} />;
    },
    ul: ({ node, ...props }: any) => <ul className="my-6 space-y-2 pl-6 list-disc marker:text-primary" {...props} />,
    ol: ({ node, ...props }: any) => (
      <ol className="my-6 space-y-3 list-decimal pl-6 text-lg text-muted-foreground marker:font-bold marker:text-foreground" {...props} />
    ),
    li: ({ node, ...props }: any) => <li className="text-lg text-muted-foreground leading-relaxed pl-2" {...props} />,
    
    blockquote: ({ node, ...props }: any) => (
      <div className="my-10 relative pl-8 pr-4 py-2 border-l-4 border-primary/30 bg-muted/30 rounded-r-xl">
        <Quote className="absolute top-2 left-2 text-primary/20 w-8 h-8 -z-10" />
        <blockquote className="text-xl font-medium italic text-foreground/80 leading-relaxed" {...props} />
      </div>
    ),
    img: ({ node, ...props }: any) => (
      <figure className="my-10 rounded-xl overflow-hidden shadow-2xl border border-border bg-muted">
        <img {...props} className="w-full h-auto object-cover" loading="lazy" />
        {props.alt && <figcaption className="p-3 text-center text-sm text-muted-foreground bg-card/50 backdrop-blur-sm border-t border-border/50">{props.alt}</figcaption>}
      </figure>
    ),
    a: ({ node, ...props }: any) => {
      const isInternal = props.href && (props.href.startsWith('/') && !props.href.startsWith('//'));
      const classes = "text-primary font-semibold hover:underline decoration-2 underline-offset-2 transition-colors";
      if (isInternal) {
        return <Link to={props.href} className={classes} {...props} />;
      }
      return <a target="_blank" rel="noopener noreferrer" className={classes} {...props} />;
    },
    code: ({ node, inline, className, children, ...props }: any) => {
      if (inline) {
        return <code className="bg-muted text-primary font-mono text-sm px-1.5 py-0.5 rounded border border-border" {...props}>{children}</code>;
      }
      return (
        <pre className="bg-slate-950 text-slate-50 p-4 rounded-xl overflow-x-auto my-6 border border-slate-800 shadow-inner">
          <code className="font-mono text-sm" {...props}>{children}</code>
        </pre>
      );
    }
  };

  return (
    <div className="prose-container max-w-none">
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {part}
          </ReactMarkdown>
          {/* Render CTA between parts, but not after the last part */}
          {index < parts.length - 1 && <CallToAction />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default MarkdownRenderer;
